import { ChatModuleType, ItemActionEnum, LiveChatModule } from 'app/copied/models/ChatModules';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import useModule from 'app/hooks/useModule';
import useUnreadMessageCount from 'app/modules/live-chat/hooks/useUnreadMessageCount';
import { useTranslation } from 'react-i18next';
import { Language } from 'app/i18n/translationModels';
import useWebchatVersion from 'app/hooks/useWebchatVersion';
import { ChatVersion } from 'app/copied/constants/common';
import useUiConfig from 'app/hooks/useUiConfig';
import { format } from 'date-fns';
import useNextOnlineTime from 'app/modules/live-chat/hooks/useNextOnlineTime';

import SingleCTA from './CTA/SingleCTA';
import useTailorTracking from '../hooks/useTailorTracking';
import { TailorLiveChatModuleUnity } from './TailorLiveChatModuleUnity';

export default function TailorLiveChatModule({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const { captureLandingPageLiveChatClicked } = useTailorTracking();
  const liveChatModule = useModule(ChatModuleType.LiveChat) as unknown as LiveChatModule;
  const { data: helpCenter } = useFetchHelpCenter();
  const unreadMessageCount = useUnreadMessageCount();
  const isUnity = useWebchatVersion() === ChatVersion.v4_1;
  const uiConfig = useUiConfig();
  const NNOnline = helpCenter?.nurses_are_online;

  const nextOnlineStatusChange = helpCenter?.valid_until_date_str || '';
  const nextOnlineTimeWithOnline = useNextOnlineTime(nextOnlineStatusChange, {
    includeOnline: true,
    dateFormatCapitalized: false,
  });

  const item = liveChatModule?.items?.find((item) => {
    if (NNOnline) {
      return item.action === ItemActionEnum.StartChat;
    }

    return item.action === ItemActionEnum.OfflineChat;
  });
  const caption = i18n.language !== Language.English ? t('only_in_english') : '';

  const icon = !NNOnline && liveChatModule?.offline_icon ? liveChatModule?.offline_icon : liveChatModule?.header_icon;
  const title =
    !NNOnline && liveChatModule?.offline_title ? liveChatModule?.offline_title : liveChatModule?.header_title;

  const statusText = NNOnline
    ? helpCenter?.valid_until_date_str
      ? `<strong>Online</strong> until ${format(new Date(helpCenter.valid_until_date_str), 'ha').toLowerCase()}`
      : '<strong>Online</strong>'
    : `<strong>Offline</strong> · ${nextOnlineTimeWithOnline}`;

  const handleCTAClick = () => {
    captureLandingPageLiveChatClicked();
  };

  if (isUnity) {
    const backgroundColor = uiConfig?.modules?.liveChat?.buttonBackgroundColor || '#F8F6FF';

    return (
      <TailorLiveChatModuleUnity
        className={className}
        icon={icon}
        title={title}
        description={item?.title}
        caption={caption}
        unreadMessageCount={unreadMessageCount}
        onClick={handleCTAClick}
        backgroundColor={backgroundColor}
        isOnline={NNOnline}
        statusText={statusText}
      />
    );
  }

  return (
    <div
      className={className}
      id="ostro-tailor-live-chat-module"
    >
      <SingleCTA
        imgSrc={icon}
        imgAlt={title}
        title={title}
        description={item?.title}
        caption={caption}
        link="/live-chat"
        unreadMessageCount={unreadMessageCount}
        onClick={handleCTAClick}
      />
    </div>
  );
}
